import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '@/context/context';
import instanceAxios from '@/util/axios';
import {
  donationsAnalyticsUrl,
  purchasesAnalyticsUrl,
  personalEventTicketsAnalyticsUrl,
  leadsAnalyticsUrl
} from '@/constants';
import { AnalyticsData } from '@sm-dashboard/types';

type AnalyticsDataHookProps = {
  type: 'donations' | 'purchases' | 'personalEventTickets' | 'leads';
  queryParams?: URLSearchParams;
};

export const useAnalyticsData = ({ type, queryParams }: AnalyticsDataHookProps) => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const { userPermission } = useAppContext();

  const searchParamsKey = searchParams.toString();
  const explicitQueryKey = queryParams === undefined ? null : queryParams.toString();
  const userOperationsKey = userPermission?.operations?.join(',') ?? '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams(searchParams);
        params.delete('_timestamp');

        // Use optimized analytics endpoints - no need for rowsPerPage manipulation
        params.delete('page');
        params.delete('rowsPerPage');
        params.delete('sortField');
        params.delete('sortDirection');
        const finalQueryParams =
          explicitQueryKey !== null ? new URLSearchParams(explicitQueryKey) : params;
        const personalOnly = Boolean(finalQueryParams.get('shaliachId'));
        const fromMonthlyFinance = finalQueryParams.get('fromMonthlyFinance') === '1';

        const analyticsUrl =
          type === 'donations'
            ? donationsAnalyticsUrl
            : type === 'purchases'
            ? purchasesAnalyticsUrl
            : type === 'leads'
            ? leadsAnalyticsUrl
            : personalEventTicketsAnalyticsUrl;
        const includeOps =
          !personalOnly && !fromMonthlyFinance && Boolean(userPermission?.operations?.length);
        const requestUrl = `${analyticsUrl}?${finalQueryParams}${
          includeOps ? `&operationsAllowed=${userPermission.operations}` : ''
        }`;

        const response = await instanceAxios(requestUrl);
        setAnalyticsData(response?.data || null);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError('שגיאה בטעינת נתוני אנליטיקות');
        setAnalyticsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParamsKey/userOperationsKey מייצבים את האפקט
  }, [searchParamsKey, type, explicitQueryKey, userOperationsKey]);

  return { analyticsData, loading, error };
};

// Hook ספציפי לתרומות (לשמירה על backward compatibility)
export const useDonationsAnalyticsData = () => {
  return useAnalyticsData({ type: 'donations' });
};

// Hook ספציפי לרכישות
export const usePurchasesAnalyticsData = () => {
  return useAnalyticsData({ type: 'purchases' });
};

export const usePersonalEventTicketsAnalyticsData = () => {
  return useAnalyticsData({ type: 'personalEventTickets' });
};

export const useLeadsAnalyticsData = () => {
  return useAnalyticsData({ type: 'leads' });
};
